import i18n from 'locales/i18n';
import { useStoreState } from 'providers/StoreStateContext';
import { PropsWithChildren, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useFetchRefreshToken } from 'services/auth';
import {
  useGetCountries,
  useGetCurrencies,
  useGetSettlementMethods,
  useGetSupportedLanguages,
} from 'services/enums';
import {
  useAppSettings,
  useDefaultCurrency,
} from 'services/settings/application/Application';
import { useGetTheme } from 'services/theme';

type ProtectedRouteProps = PropsWithChildren;

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const {
    token,
    refreshToken,
    tokenData,
    setCountries,
    setLanguages,
    setSettlementMethods,
    setCurrencies,
    setDefaultCurrency,
    setApplicationSettings,
    isTokenStale,
  } = useStoreState();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { mutateAsync } = useFetchRefreshToken();
  const { t } = useTranslation();
  const currentTheme = useGetTheme();

  const countriesEnum = useGetCountries();
  const languagesEnum = useGetSupportedLanguages();
  const settlementMethodEnum = useGetSettlementMethods();
  const currenciesEnum = useGetCurrencies();

  const getAppSettings = useAppSettings();
  const getDefaultCurrency = useDefaultCurrency();

  const refreshRefreshToken = async (refreshToken: string) =>
    await mutateAsync({ token: refreshToken });

  const refreshTokens = async (exp: number) => {
    const tokenExp = (exp - 90) * 1000;
    if (tokenExp < Date.now()) {
      console.log('Token expired, refreshing...');
      if (!refreshToken) return;
      await refreshRefreshToken(refreshToken);
    }
  };

  useEffect(() => {
    if (!tokenData && !refreshToken) {
      navigate('/login', { replace: true });
    }
    if (!isTokenStale && refreshToken) {
      refreshRefreshToken(refreshToken);
    }

    if (tokenData && refreshToken) {
      const timeout = (tokenData.exp - 60) * 1000 - Date.now();

      if (timeout > 0) {
        console.log(`Token expires in: ${Math.round(timeout / 60000)} min`);
      }

      const timerId = setTimeout(() => refreshTokens(tokenData.exp), timeout);

      return () => {
        clearTimeout(timerId);
      };
    }
  }, [refreshToken, token, isTokenStale]);

  useEffect(() => {
    if (
      getAppSettings.data?.settlementStartMonth === null &&
      pathname !== '/settings/application'
    ) {
      toast.error(t('toast-message.fufill-data-in-settings'));
      navigate('/settings/application');
    }
  }, [getAppSettings.data, navigate, pathname]);

  useEffect(() => {
    if (tokenData) {
      if (countriesEnum.data) {
        setCountries(countriesEnum.data);
      }
      if (languagesEnum.data) {
        setLanguages(languagesEnum.data);
      }
      if (settlementMethodEnum.data) {
        setSettlementMethods(settlementMethodEnum.data);
      }
      if (currenciesEnum.data) {
        setCurrencies(currenciesEnum.data);
      }
      if (getAppSettings.data) {
        setApplicationSettings(getAppSettings.data);

        const language =
          getAppSettings.data.defaultApplicationLanguage.split('-')[0];
        i18n.changeLanguage(language);
        localStorage.setItem('language', language);
      }
      if (getDefaultCurrency.data) {
        setDefaultCurrency(getDefaultCurrency.data);
      }
    }
  }, [
    getAppSettings.data,
    countriesEnum.data,
    currenciesEnum.data,
    settlementMethodEnum.data,
    languagesEnum.data,
    getDefaultCurrency.data,
    refreshToken,
    token,
  ]);

  return <>{children}</>;
}
